import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'
import Toast from './Toast'
import { AxiosError } from 'axios'
import { useExcludeTasksMutation } from '@qc_task_list_view/hooks/useExcludeTasksMutation'
import ArrowTooltips from '@common/components/Tooltip'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from '@common/components/Form/TextField'
import { PopupState } from 'material-ui-popup-state/hooks'
import { DeleteSweepRounded } from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { HTTPValidationError } from '@common/config/api/client'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: SPACING.borderRadiusXl,
  bgcolor: 'background.default',
  boxShadow: 8,
  padding: SPACING.minSpacing,
}

const ExcludeValidationSchema = z.object({
  reason: z.string().min(1),
})

export default function ExcludeModal({
  task_id,
  pseudo_id,
  study_id,
  popupState,
}: {
  task_id: string
  pseudo_id: string
  study_id: string
  popupState?: PopupState
}) {
  const {
    control,
    watch,
    formState: { isValid },
    setError,
  } = useForm({ mode: 'onChange', defaultValues: { reason: '' }, resolver: zodResolver(ExcludeValidationSchema) })
  const reason = watch('reason')
  const [open, setOpen] = useState(false)
  const handleClose = () => {
    setOpen(false)
    popupState?.close()
  }
  const { mutateAsync: excludeTask } = useExcludeTasksMutation()

  const handleExcludeTask = async (task_ids: string[], study_id: string, reason: string) => {
    if (isValid) {
      await excludeTask({
        status_change_reason: reason,
        qc_task_ids: task_ids,
        study_id: study_id,
      })
        .then((res) => {
          toast(
            <Toast
              message="You successfully excluded task"
              task_ids={res.processed_task_ids.map((task) => {
                return task.split('-')[0]
              })}
              variant="success"
              data-testid="success-toast"
            />,
          )
          handleClose()
        })
        .catch((error: AxiosError<HTTPValidationError>) => {
          const errorMessage = Array.isArray(error.response?.data?.detail)
            ? error.response?.data?.detail.map((err) => err.msg).join(', ')
            : error.response?.data?.detail || error.message
          toast(<Toast message={errorMessage} variant="error" data-testid="error-toast" />)
        })
    } else {
      setError('reason', { message: 'Provide a reason why you need to exclude the task' })
    }
  }

  return (
    <>
      <Box
        onClick={() => {
          setOpen(true)
        }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1.5,
          alignItems: 'center',
        }}
        data-testid="exclude-task-button"
      >
        {popupState ? (
          <Fragment>
            <Typography variant="body1">Exclude</Typography>
            <ArrowTooltips message={'Exclude task. You will remove QC task from the list.'} />
          </Fragment>
        ) : (
          <ArrowTooltips
            message={'Exclude task. You will remove QC task from the list.'}
            children={
              <IconButton>
                <DeleteSweepRounded fontSize="small" />
              </IconButton>
            }
          />
        )}
      </Box>
      <Modal
        open={open}
        data-testid="exclude-modal"
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              padding: SPACING.spacingLg,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: SPACING.spacingSm,
              alignSelf: 'stretch',
              width: SIZING.boxModalWidth,
            }}
          >
            <Typography variant="h6" color="text.primary">
              {`Exclude task №${pseudo_id}?`}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Briefly explain why you want to exclude this task.
            </Typography>
          </Box>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              padding: SPACING.spacingLg,
            }}
            noValidate
            autoComplete="off"
          >
            <TextField
              name="reason"
              control={control}
              id="outlined-multiline-static"
              label="Provide a reason"
              multiline
              rows={6}
              fullWidth
              value={reason}
              onKeyDown={(e) => e.stopPropagation()}
              data-testid="reason-textfield"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              alignSelf: 'stretch',
              padding: SPACING.spacingLg,
              gap: SPACING.minSpacing,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="cancel-exclude-task-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              onClick={() => handleExcludeTask([task_id], study_id, reason)}
              data-testid="confirm-exclude-task-button"
            >
              Exclude task
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
